import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Fragment, useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";

import type {
  ApplicantTag,
  ColdSearchResult,
  PipelineStage,
  Schedule,
  VacancyColdSearchResult,
} from "@/types/domain";

import {
  addColdSearchResultToTalentPool,
  dismissColdSearchResult,
  fetchApplicantTags,
  fetchPipelineStages,
  fetchVacancy,
  fetchVacancyColdSearchResults,
  getColdSearch,
  listColdSearches,
  sendColdSearchResultToPeopleForce,
  startColdSearch,
} from "@/api/queries";
import ColdResultActions from "@/components/ColdResultActions";
import ScoreBadge from "@/components/ScoreBadge";
import {
  foundInOurDatabase,
  isDismissed,
  isInternalOrigin,
  originSource,
  sortStages,
  sourceLabel,
} from "@/lib/coldSearch";
import { ROBOTA_RUBRICS } from "@/lib/robotaRubrics";
import { safeAlert, safeHaptic } from "@/lib/telegramSafe";
import {
  WORKUA_OPEN_POLL_ACCUMULATED_MS,
  WORKUA_OPEN_POLL_MS,
  anyWorkUaOpenInFlight,
  workUaOpenApplies,
  workUaOpenSettledPaid,
} from "@/lib/workuaOpen";

/** Human elapsed time «3 хв 20 с» / «45 с» for the running-search timer. */
function formatElapsed(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  const m = Math.floor(s / 60);
  const rem = s % 60;
  return m > 0 ? `${m} хв ${rem} с` : `${rem} с`;
}

/** The floating «останні N днів» window: what the picker seeds with, what it
 * is re-seeded to after a run that went out with an empty date, and the window
 * an empty date sends. One constant because those three must agree. */
const DEFAULT_SINCE_DAYS = 14;

/** Local YYYY-MM-DD for «N days ago» — the date picker's default two-week
 * window (toISOString would shift the date across midnight in UTC±). */
function isoDaysAgo(days: number): string {
  const d = new Date(Date.now() - days * 86_400_000);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** Days back from now for the «Активність з дати» picker, clamped to the
 * backend's accepted 1..365. ``undefined`` for input the backend would reject;
 * an EMPTY picker never reaches here — the caller sends the default window for
 * it, because «порожньо» means «типові 2 тижні», not «без обмежень». */
function postedWithinDays(sinceDate: string): number | undefined {
  if (!sinceDate) return undefined;
  const ts = new Date(sinceDate).getTime();
  if (!Number.isFinite(ts)) return undefined;
  const days = Math.ceil((Date.now() - ts) / 86_400_000);
  return Math.min(365, Math.max(1, days));
}

/** One «Вік» bound as a number, or undefined when the box is empty or holds
 * something the backend would reject (it accepts 14..99). Out-of-range input
 * is DROPPED, not clamped: silently turning a typed "7" into 14 would run a
 * search the recruiter never asked for. ``Number("")`` is 0, so the emptiness
 * check must come first. Mirrors ``parseAge`` in the web admin. */
function ageBound(value: string): number | undefined {
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  const n = Number(trimmed);
  if (!Number.isFinite(n)) return undefined;
  const age = Math.trunc(n);
  return age >= 14 && age <= 99 ? age : undefined;
}

/** «Стать» as the backend enum, or ``null`` for «Не важливо».
 *
 * ``null``, not ``undefined``: this form is prefilled from the vacancy, so
 * «Не важливо» is the recruiter's decision for this run and must override a
 * gender declared on the card. An omitted key would let the card win. */
function genderParam(value: string): "male" | "female" | null {
  return value === "male" || value === "female" ? value : null;
}

/** «Досвід від N років» → the backend's ``experience_min`` (0..60).
 * Out-of-range or unparseable reads as «no floor» rather than a 422 the
 * recruiter cannot act on. Mirrors ``parseExperience`` in the web admin. */
function experienceBound(value: string): number | undefined {
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  const n = Number(trimmed.replace(",", "."));
  if (!Number.isFinite(n) || n < 0 || n > 60) return undefined;
  return n;
}

/** Where the work happens; mirrors the vacancy's own ``work_format``.
 * «Тільки віддалена» searches country-wide and drops the city — remote work
 * is not bound to a branch (a city-scoped remote query on Robota.ua returns
 * ~18x fewer résumés than a nationwide one). */
type WorkFormat = "city" | "city_region" | "remote";
/** Robota.ua ``searchType``. Work.ua has a single plain-text search and
 * ignores this — the hint text says so rather than implying both sites. */
type SearchScope = "default" | "speciality" | "everywhere";

// «Графік роботи». Same order and labels as the web admin's constraints
// card; the two Work.ua also supports lead, which is also the common ask.
const SCHEDULE_OPTIONS: { value: Schedule; label: string }[] = [
  { value: "full", label: "Повна" },
  { value: "part", label: "Неповна" },
  { value: "remote", label: "Віддалена" },
  { value: "internship", label: "Стажування" },
  { value: "project", label: "Проєктна" },
  { value: "seasonal", label: "Сезонна" },
];

/** «Нове»-badge inputs of a row. Accumulated rows carry them; per-run rows
 * (the «лише останній пошук» view and the degraded fallback) do not. */
function accumulatedMeta(row: ColdSearchResult | VacancyColdSearchResult): {
  isNew: boolean;
  firstSeenAt: string | null;
} {
  return "is_new" in row
    ? { isNew: row.is_new, firstSeenAt: row.first_seen_at }
    : { isNew: false, firstSeenAt: null };
}

/** Short «21.07» for the muted «вперше знайдено» hint. */
function shortDate(iso: string): string {
  return new Date(iso).toLocaleDateString("uk-UA", {
    day: "2-digit",
    month: "2-digit",
  });
}

/** Rows per page. Matches the backend's ``le=200`` ceiling; the accumulated
 * list can exceed it, which is why the screen pages. */
const RESULT_LIMIT = 200;

export default function ColdSearchScreen(): JSX.Element {
  const { vacancyId } = useParams();
  const [params] = useSearchParams();
  const vacancyTitle = params.get("title");
  const qc = useQueryClient();
  const [coldSearchId, setColdSearchId] = useState<string | null>(null);
  const [filters, setFilters] = useState({
    // The vacancy title is the natural first query — prefill it so the
    // recruiter can just hit «Запустити пошук».
    keywords: vacancyTitle ?? "",
    city: "",
    specialization: "",
    // Age/gender stay strings here and are parsed at submit (same idiom as
    // ``sinceDate``): an empty box must mean «no limit», not 0.
    ageMin: "",
    ageMax: "",
    gender: "",
    // Where the work happens. Prefilled from the vacancy's own work format
    // below; 'city' is the neutral value every run used before the vacancy
    // could declare one.
    workFormat: "city" as WorkFormat,
    // Robota.ua ``searchType`` — how strictly the query is matched.
    searchScope: "default" as SearchScope,
    experienceMin: "",
    // «Графік роботи» — multi-select, prefilled from the vacancy below.
    // Empty = не задано; sent explicitly at submit like age/gender, so
    // clearing it here really does clear it for this run.
    schedule: [] as Schedule[],
  });
  // The vacancy's declared constraints. Without this read the screen could
  // only send «нічого не сказано» for them, so the recruiter could see the
  // search narrow itself with no control showing why — and «Не важливо»
  // here could never override a constraint set on the card.
  const vacancy = useQuery({
    queryKey: ["vacancy", vacancyId],
    queryFn: () => fetchVacancy(vacancyId!),
    enabled: !!vacancyId,
    retry: false,
    staleTime: 60_000,
  });
  const [prefilled, setPrefilled] = useState(false);
  // Картка вакансії ще читається. Окремий прапорець, а не «unclaimed для
  // undefined»: доки вакансія невідома, ми не знаємо й того, що вона вільна,
  // і показали б хибну вимогу забрати її собі. Саме ``isLoading``, а не
  // ``isPending``: вимкнений запит (немає ``vacancyId``) лишається pending
  // назавжди і замкнув би кнопку намертво. Помилка читання сюди НЕ входить —
  // вона не мусить забирати в рекрутера запуск; за неї відповідає гейт
  // ``prefilled`` у сабміті, який не пускає в запит порожні обмеження.
  const vacancyLoading = vacancy.isLoading;
  // Claim-first (мітинг 2026-08-18): сервер 403-ить запуск на вільній
  // вакансії — дзеркалимо це в кнопці, щоб вона пояснювала себе сама.
  const savedSince = vacancy.data?.search_activity_since ?? null;
  const unclaimed =
    !!vacancy.data && (vacancy.data.recruiter_id ?? null) === null;
  // Recency window as a calendar date («Активність з дати», client QA
  // 2026-07-22): default two weeks back, cleared = no recency limit.
  // The axis is the VENDOR's, and the two vendors disagree: Robota.ua cuts
  // on last activity, Work.ua on résumé creation. The label names the one
  // the recruiter is more likely to be surprised by (2026-08-18: a résumé
  // «від 6 липня» came back under a «з 14.08» window — active seeker, old CV).
  const [sinceDate, setSinceDate] = useState(() =>
    isoDaysAgo(DEFAULT_SINCE_DAYS),
  );
  // Мітинг 2026-08-18: змінена дата фіксується на вакансії (activity_since
  // у сабміті). Порожня дата — те, чим раніше була кнопка резету: знімає
  // зафіксовану й повертає плаваючі «2 тижні».
  const [sinceTouched, setSinceTouched] = useState(false);
  // Ticks every second while a search runs so the elapsed timer updates
  // (meeting 2026-07-08: recruiter wanted to see how long the search takes).
  const [nowTs, setNowTs] = useState(() => Date.now());
  // The pipeline-stage name chosen at push time, keyed by result id. The
  // refetched ``ColdSearchResult`` carries ``pushed_to_pf_at`` but no
  // stage, so we remember the label locally to render it on the pushed
  // card (see open issue: this label does not survive a full reload).
  const [pushedStageById, setPushedStageById] = useState<
    Record<string, string>
  >({});
  // «Лише останній пошук» — off by default: the card shows every run of the
  // vacancy merged (client feedback 2026-07-28, nightly runs of 2-3 résumés
  // used to wipe yesterday's list off the screen).
  const [onlyLatest, setOnlyLatest] = useState(false);
  // «Показати відхилених» — both reads hide the «Не цікавий» trash by default.
  // The web admin got the switch first; ADR-010 makes this the surface the
  // recruiters actually work in, so a control only the web has reaches nobody.
  const [showDismissed, setShowDismissed] = useState(false);
  // «Показати нижче порога» — ШІ вже оцінив ці резюме, а гейт новизни більше
  // ніколи їх не запропонує, тож поріг ховав уже оплачену роботу. Саме це
  // означало «АІ не підтягнув, знайшла руками» (Наталя, 21.08).
  const [showBelowThreshold, setShowBelowThreshold] = useState(false);
  // «Показати відсіяних» — те, що прибрала сама картка: чуже місто й резюме
  // зовсім іншої галузі на рядку без оцінки. Вік і стать сюди не входять:
  // це обмеження, які людина вписала у вакансію.
  const [showFilteredOut, setShowFilteredOut] = useState(false);
  // Page cursor for the row list. The accumulated view grows without bound
  // (nothing is deleted between nightly runs), so a hard 200-row read with no
  // pager made every candidate past #200 unreachable from the Mini App —
  // which is the primary recruiter UI (ADR-010).
  const [offset, setOffset] = useState(0);
  // The accumulated read is the only source of rows in the default view, and
  // the pinned run is fetched with limit=1 (it only feeds the status header).
  // If the accumulated read fails we must widen that pinned read back to a
  // full page, or the recruiter is left staring at exactly one row.
  const [accumulatedFailed, setAccumulatedFailed] = useState(false);

  // Prefill once from the vacancy's declared constraints, so this form
  // shows the same narrowing the nightly run applies. Everything stays
  // editable — an edit here overrides the card for this run only.
  useEffect(() => {
    const v = vacancy.data;
    if (prefilled || !v) return;
    setFilters((f) => ({
      ...f,
      keywords: f.keywords || (v.title ?? ""),
      workFormat: v.work_format ?? "city",
      gender: v.gender_preference ?? "",
      ageMin: v.age_min != null ? String(v.age_min) : "",
      ageMax: v.age_max != null ? String(v.age_max) : "",
      // Joined the card on 2026-08-10. An empty box still defers to the
      // vacancy (the key is omitted, not sent as null), so this is about
      // honesty rather than behaviour: without it the screen shows «будь-який
      // досвід» over a run that will in fact be narrowed.
      experienceMin: v.experience_min != null ? String(v.experience_min) : "",
      schedule: [...(v.schedule ?? [])],
    }));
    // Дата, яку власник зафіксував раніше, б'є плаваючий дефолт — але не
    // дату, яку рекрутер встиг увести, поки вакансія ще вантажилась.
    if (v.search_activity_since && !sinceTouched) {
      setSinceDate(v.search_activity_since);
    }
    setPrefilled(true);
  }, [prefilled, vacancy.data, sinceTouched]);

  const ageMinValue = ageBound(filters.ageMin);
  const ageMaxValue = ageBound(filters.ageMax);
  // The backend 422s an inverted window — block the run and say why instead
  // of surfacing a validation error through the generic alert.
  const ageRangeInvalid =
    ageMinValue !== undefined &&
    ageMaxValue !== undefined &&
    ageMaxValue < ageMinValue;

  const start = useMutation({
    mutationFn: () =>
      startColdSearch(vacancyId!, {
        // Backend ``ColdSearchFilters.keywords`` is a plain string —
        // the old array form 422'd on the first real run.
        keywords: filters.keywords.trim() || undefined,
        city: filters.city.trim() || undefined,
        // Порожня дата йде ПЕРШОЮ гілкою: очищення поля теж робить
        // sinceTouched=true, тож інакше запуск пішов би з activity_since:null
        // і БЕЗ вікна — тобто мовчки без обмеження свіжості. Порожньо =
        // «зняти зафіксовану дату» (явний null) + типові два тижні на цей
        // запуск. Далі: торкнута/зафіксована дата шле activity_since (вона
        // персистується на вакансії), недоторканий дефолт — лише дні.
        ...(!sinceDate
          ? {
              activity_since: null,
              posted_within_days: DEFAULT_SINCE_DAYS,
            }
          : sinceTouched || savedSince
            ? { activity_since: sinceDate }
            : { posted_within_days: postedWithinDays(sinceDate) }),
        specialization: filters.specialization || undefined,
        // Explicit, including nulls: the boxes were prefilled from the
        // vacancy, so what they hold at submit time is a decision. Omitting
        // a key hands it back to the vacancy, which would make clearing a
        // constraint here impossible.
        //
        // ``prefilled`` is the premise of that sentence, so it gates it.
        // Before the prefill effect runs these boxes hold component defaults,
        // not decisions, and sending them clears every constraint of the
        // vacancy for this run — and permanently: the stored value then
        // differs from the ``_vacancy_prefill`` stamp, so the nightly
        // ``refresh_vacancy_derived_filters`` treats it as a recruiter's
        // choice and never hands the constraint back. Omitting costs nothing:
        // the server fills an absent key from the vacancy itself
        // (``apply_vacancy_constraints``), which is what the web form has
        // always relied on.
        ...(prefilled
          ? {
              age_min: ageMinValue ?? null,
              age_max: ageMaxValue ?? null,
              gender: genderParam(filters.gender),
              remote_only: filters.workFormat === "remote",
              include_region: filters.workFormat === "city_region",
              experience_min: experienceBound(filters.experienceMin),
              // Explicit like age/gender above: prefilled from the vacancy,
              // so an emptied list here is a decision the vacancy must not
              // take back.
              schedule: filters.schedule,
            }
          : {}),
        // Omitted when it is the vendor default, so stored filters stay
        // comparable with runs created before this control existed.
        search_scope:
          filters.searchScope === "default" ? undefined : filters.searchScope,
      }),
    onSuccess: (cs) => {
      setColdSearchId(cs.id);
      setOffset(0);
      setSinceTouched(false);
      // Порожня дата — разова дія, а не стан: вікно вже пішло з запуском, і
      // залишена порожньою форма наступного разу знову тихо зняла б
      // зафіксовану дату. Функційний апдейт, щоб не залежати від замикання.
      setSinceDate((d) => d || isoDaysAgo(DEFAULT_SINCE_DAYS));
      // Запуск міг (пере)зафіксувати чи очистити search_activity_since.
      void qc.invalidateQueries({ queryKey: ["vacancy", vacancyId] });
      // coldSearchId живе лише до виходу з екрана (а з імені кандидата тепер
      // виходять на кожному кроці). Повернувшись, екран бере «останній
      // прогін» — і без цього ще 30 с брав би з кешу попередній.
      void qc.invalidateQueries({
        queryKey: ["cold-searches-latest", vacancyId],
      });
    },
    onError: (err: Error) => {
      // Surface backend guards (e.g. closed-vacancy 409) instead of failing
      // silently — otherwise tapping «Запустити пошук» just does nothing.
      safeHaptic("error");
      safeAlert(`Не вдалося запустити пошук: ${err.message}`);
    },
  });

  // Component state dies on unmount, but the search keeps running on
  // the backend — leaving the screen and coming back used to show a
  // blank «Запустити пошук» form while the run was still in flight.
  // Resume the recruiter's most recent search for this vacancy instead.
  const latest = useQuery({
    queryKey: ["cold-searches-latest", vacancyId],
    queryFn: () => listColdSearches(vacancyId!),
    enabled: !!vacancyId && !coldSearchId,
    staleTime: 30_000,
    // A 404/401 here is terminal, not transient — three silent retries only
    // delayed the (previously invisible) failure.
    retry: false,
  });
  const activeId = coldSearchId ?? latest.data?.[0]?.id ?? null;

  // A vacancy fed only by a Work.ua mailing has no pinned run («latest» lists
  // searches only), so a failed accumulated read has nothing to fall back to:
  // it keeps its last page instead of emptying the screen.
  const perRunOnly = onlyLatest || (accumulatedFailed && !!activeId);
  // Pinned run: a single row is enough to drive the status header and the
  // elapsed timer at 2s; it becomes the row source only in the per-run view.
  const detailLimit = perRunOnly ? RESULT_LIMIT : 1;
  // ``offset`` is the ACCUMULATED page cursor unless the per-run list is the
  // one being paged — forwarding it would refetch the pinned run at
  // ``offset=200`` on every page turn for numbers that don't depend on it.
  const detailOffset = perRunOnly ? offset : 0;

  const detail = useQuery({
    // ``showDismissed`` belongs in the key: without it the first flip of the
    // toggle re-serves the cached narrow page and reads as a dead control.
    queryKey: [
      "cold-search",
      activeId,
      detailLimit,
      detailOffset,
      showDismissed,
      showFilteredOut,
    ],
    queryFn: () =>
      getColdSearch(activeId!, {
        limit: detailLimit,
        offset: detailOffset,
        includeDismissed: showDismissed,
        includeFilteredOut: showFilteredOut,
      }),
    enabled: !!activeId,
    retry: false,
    refetchInterval: (q) => {
      // A persistent error (401/404/500) must stop the 2s loop. An errored
      // query keeps ``data`` undefined, so without this branch the ternary
      // below returns 2000 forever and the screen hammers a dead endpoint
      // every two seconds for as long as it is open.
      if (q.state.status === "error") return false;
      const status = q.state.data?.cold_search.status;
      if (status !== "completed" && status !== "failed") return 2000;
      // Only in the per-run view does this read carry the rows; otherwise
      // it holds a single row and the accumulated read does the watching.
      return perRunOnly && anyWorkUaOpenInFlight(q.state.data?.results)
        ? WORKUA_OPEN_POLL_MS
        : false;
    },
  });

  // ``isError`` first: falling back to "pending" on a failed read pins the
  // screen in a permanent fake «пошук триває» — spinner on, start button
  // disabled, and ``running`` stuck true, which is what keeps the heavy
  // accumulated poll alive as well.
  const runStatus = detail.isError
    ? null
    : (detail.data?.cold_search.status ?? (activeId ? "pending" : null));
  const running = runStatus === "pending" || runStatus === "running";

  // Every run of this vacancy merged into one candidate list. Polled at 15s
  // (never the detail endpoint's 2s — this one counts over the vacancy's
  // whole result history) and invalidated the moment the run completes.
  const accumulated = useQuery({
    queryKey: [
      "cold-search-accumulated",
      vacancyId,
      offset,
      showDismissed,
      showBelowThreshold,
      showFilteredOut,
    ],
    queryFn: () =>
      fetchVacancyColdSearchResults(vacancyId!, {
        limit: RESULT_LIMIT,
        offset,
        includeDismissed: showDismissed,
        includeBelowThreshold: showBelowThreshold,
        includeFilteredOut: showFilteredOut,
      }),
    // Not gated on ``activeId``: «latest» lists manual/nightly searches only,
    // so a vacancy fed by nothing but a Work.ua mailing — whose Telegram card
    // links straight here — has no pinned run, and its rows would never be
    // read. The gate's old reason, this endpoint 404ing a recruiter who never
    // searched here, went with the shared vacancy corpus (2026-08-14).
    enabled: !!vacancyId && !onlyLatest,
    retry: false,
    // ``retry: false`` does not stop interval refetches, and this endpoint
    // counts over the vacancy's whole result history — stop on a hard error.
    refetchInterval: (q) => {
      if (q.state.status === "error") return false;
      return running || anyWorkUaOpenInFlight(q.state.data?.results)
        ? WORKUA_OPEN_POLL_ACCUMULATED_MS
        : false;
    },
  });

  useEffect(() => {
    setAccumulatedFailed(accumulated.isError);
  }, [accumulated.isError]);

  // Depend on the status STRING, never on ``detail.data`` — the object
  // identity changes on every 2s poll and would invalidate in a loop.
  useEffect(() => {
    if (detail.data?.cold_search.status !== "completed") return;
    void qc.invalidateQueries({
      queryKey: ["cold-search-accumulated", vacancyId],
    });
  }, [detail.data?.cold_search.status, vacancyId, qc]);

  // Pipeline stages live behind their own endpoint (not the polled
  // detail). One fetch per cold search; a failure or empty result
  // degrades «Цікавий» to a candidate-only push (Phase-1 behaviour).
  // The stages are the vacancy's, read through any of its runs: on a vacancy
  // with mailing runs only there is no pinned run to read them through.
  const stagesRunId =
    activeId ?? accumulated.data?.results[0]?.cold_search_id ?? null;
  const stagesQuery = useQuery({
    queryKey: ["pipeline-stages", stagesRunId],
    queryFn: () => fetchPipelineStages(stagesRunId!),
    enabled: !!stagesRunId,
    retry: false,
    staleTime: Infinity,
  });

  // «Кадровий резерв»: PeopleForce takes only tag IDS and has no create-tag
  // endpoint, so the recruiter picks from the tenant's own tags. Tenant-wide
  // and static — one fetch per mount, not per card.
  const tagsQuery = useQuery({
    queryKey: ["peopleforce-applicant-tags"],
    queryFn: fetchApplicantTags,
    // Same gate as ``stagesQuery``: without a run there are no cards, so no
    // picker to fill — and the fetch is a PeopleForce round-trip server-side.
    enabled: !!stagesRunId,
    retry: false,
    staleTime: Infinity,
  });

  // Both action endpoints take a (cold_search_id, result_id) PAIR and reject
  // a foreign one, so the run id must come from the ROW. In the accumulated
  // view most rows belong to older runs — passing the pinned ``activeId``
  // would 404 every one of them.
  const invalidateResults = (): void => {
    void qc.invalidateQueries({ queryKey: ["cold-search", activeId] });
    void qc.invalidateQueries({
      queryKey: ["cold-search-accumulated", vacancyId],
    });
    // The name opens the candidate card, which caches the same row: without
    // this it would offer «Цікавий» again for a while and draw a 409. A push
    // also creates the application the card's status and selector read.
    void qc.invalidateQueries({ queryKey: ["candidate-cold-result"] });
    void qc.invalidateQueries({ queryKey: ["candidate-apps"] });
  };

  const send = useMutation({
    mutationFn: (vars: {
      coldSearchId: string;
      resultId: string;
      applicantStateId?: number;
    }) =>
      sendColdSearchResultToPeopleForce(
        vars.coldSearchId,
        vars.resultId,
        vars.applicantStateId,
      ),
    onSuccess: () => {
      safeHaptic("success");
      invalidateResults();
    },
    onError: (err: Error) => {
      safeHaptic("error");
      safeAlert(`Не вдалося надіслати в PeopleForce: ${err.message}`);
    },
  });

  const dismiss = useMutation({
    mutationFn: (vars: { coldSearchId: string; resultId: string }) =>
      dismissColdSearchResult(vars.coldSearchId, vars.resultId),
    onSuccess: () => {
      safeHaptic("success");
      invalidateResults();
    },
    onError: (err: Error) => {
      safeHaptic("error");
      safeAlert(`Не вдалося приховати: ${err.message}`);
    },
  });

  const addToPool = useMutation({
    mutationFn: (vars: {
      coldSearchId: string;
      resultId: string;
      tagId: number;
    }) =>
      addColdSearchResultToTalentPool(
        vars.coldSearchId,
        vars.resultId,
        vars.tagId,
      ),
    onSuccess: () => {
      safeHaptic("success");
      invalidateResults();
    },
    onError: (err: Error) => {
      safeHaptic("error");
      safeAlert(`Не вдалося додати в кадровий резерв: ${err.message}`);
    },
  });

  // Drive the running-search elapsed timer: tick once a second only while a
  // search is pending/running (stops on completion — and on a failed read, or
  // it would re-render every second forever behind a dead endpoint).
  useEffect(() => {
    if (!running) return;
    const id = window.setInterval(() => setNowTs(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [running]);

  if (!vacancyId) return <div className="p-4">Невірний id вакансії.</div>;

  const coldSearch = detail.data?.cold_search;
  // Same source as ``running`` on purpose — a second, isError-blind copy of
  // this derivation is exactly what stranded the screen on a stuck spinner.
  const isSearching = running;
  const startedAtMs = coldSearch?.created_at
    ? new Date(coldSearch.created_at).getTime()
    : null;
  const elapsedSeconds =
    startedAtMs !== null ? (nowTs - startedAtMs) / 1000 : null;
  // The read that owns the list right now: the score-gate facts in the header
  // must always describe the rows actually on screen.
  const activeEnvelope = perRunOnly ? detail.data : accumulated.data;
  const accumulatedData = perRunOnly ? undefined : accumulated.data;
  const appliedMinScore = activeEnvelope?.applied_min_score;
  const thresholdRelaxed = activeEnvelope?.threshold_relaxed === true;
  const totalMatched = activeEnvelope?.total_matched;
  // Рядки на екрані взагалі без оцінки («резюме не отримали»). Поріг їх
  // пропускає за задумом — вони ще не оцінені, а не низькі, — тому
  // «за порогом >= 4.5 показано 4» приписувало ШІ двох людей, яких він
  // жодного разу не дивився (Наталя, 25.08). Тепер це два різні числа.
  const pendingCount = activeEnvelope?.pending_count ?? 0;
  // Наталя, 25.08: половина картки «Автослюсара» (Львів) була з Києва —
  // рядки червневих прогонів, коли внутрішній канал ще не отримував міста
  // вакансії. Тепер їх немає, але мовчазне падіння «накопичено» читалось би
  // як втрата даних, тому число називаємо.
  const hiddenOutOfTown = accumulatedData?.hidden_out_of_town ?? 0;
  const hiddenOffField = accumulatedData?.hidden_off_field ?? 0;
  // Both envelopes carry it: the ШІ-префільтр of a Work.ua mailing hides rows
  // by résumé title alone, so it must stay reversible from either view.
  const hiddenPrefiltered = activeEnvelope?.hidden_prefiltered ?? 0;
  const hiddenFilteredOut =
    hiddenOutOfTown + hiddenOffField + hiddenPrefiltered;
  // Kept OUT of `hiddenFilteredOut`: that number labels «Показати відсіяних»
  // and must stay a promise about rows the checkbox reveals. The schedule the
  // recruiter ticked on the vacancy is not lifted by it, so the rows stay
  // hidden — named in the «Приховано» sentence instead of vanishing silently.
  const hiddenOffSchedule = accumulatedData?.hidden_off_schedule ?? 0;
  const hiddenTotal = hiddenFilteredOut + hiddenOffSchedule;
  // Скільки зведених кандидатів зараз ховає поріг. Обидва числа беруться з
  // того самого конверта й того самого статус-гейта, тож різниця — це рівно
  // те, що відкриє галочка. Нуль, коли галочка вже увімкнена.
  const belowThresholdHidden = Math.max(
    0,
    (accumulatedData?.candidates_total ?? 0) -
      (accumulatedData?.total_matched ?? 0),
  );
  const stages = sortStages(stagesQuery.data?.stages ?? []);
  const defaultStageId = stagesQuery.data?.default_stage_id ?? null;
  // Page of rows exactly as the server returned it — the pager reasons about
  // this one, the list renders the dismissed-filtered view of it. The
  // accumulated endpoint already gates dismissed candidates out server-side
  // (across ALL runs); the filter stays as belt-and-braces for the per-run
  // view — and must lift with the toggle, or «Показати відхилених» would ask
  // the server for the trash and then drop it here.
  const pageRows: (ColdSearchResult | VacancyColdSearchResult)[] =
    activeEnvelope?.results ?? [];
  const results = showDismissed
    ? pageRows
    : pageRows.filter((r) => r.status !== "dismissed");
  const rowsFetching = perRunOnly ? detail.isFetching : accumulated.isFetching;
  // Every row of a vacancy's FIRST run is ``is_new`` — a card where all rows
  // are «Нове» carries no information, so the badge waits for run two.
  const showNewBadge = (accumulatedData?.runs_total ?? 0) > 1;
  // Rows without a pinned run: only a Work.ua mailing lands them (it is not a
  // search, so «latest» never lists it).
  const mailingOnly = !activeId && (accumulated.data?.runs_total ?? 0) > 0;
  const showRunPanel = !!activeId || mailingOnly;
  // The server 403s every open on an unclaimed vacancy; a foreign owner is
  // not known here, so that one learns it from the server's message.
  const mayOpenWorkUa = !unclaimed;

  return (
    <div className="flex flex-col gap-4 p-4 pb-24">
      <Link
        to={
          vacancyTitle
            ? `/vacancy/${vacancyId}/candidates?title=${encodeURIComponent(vacancyTitle)}`
            : "/shortlist"
        }
        className="text-xs text-tg-link"
      >
        {vacancyTitle ? "← До кандидатів" : "← До шортлисту"}
      </Link>
      <header>
        <h1 className="text-lg font-semibold">Холодний пошук</h1>
        {vacancyTitle && (
          <p className="truncate text-sm text-tg-hint">{vacancyTitle}</p>
        )}
      </header>

      <div className="rounded border border-tg-secondary p-3">
        <label
          htmlFor="cold-search-keywords"
          className="block text-xs uppercase text-tg-hint"
        >
          Пошуковий запит
        </label>
        <input
          id="cold-search-keywords"
          name="keywords"
          value={filters.keywords}
          onChange={(e) =>
            setFilters((f) => ({ ...f, keywords: e.target.value }))
          }
          placeholder="напр. продавець-консультант"
          className="mt-1 w-full rounded border border-tg-secondary bg-tg-bg p-2 text-sm"
        />
        <label
          htmlFor="cold-search-city"
          className="mt-2 block text-xs uppercase text-tg-hint"
        >
          Місто
        </label>
        <input
          id="cold-search-city"
          name="city"
          value={filters.city}
          onChange={(e) => setFilters((f) => ({ ...f, city: e.target.value }))}
          placeholder="напр. Київ"
          className="mt-1 w-full rounded border border-tg-secondary bg-tg-bg p-2 text-sm"
        />
        <label
          htmlFor="cold-search-work-format"
          className="mt-2 block text-xs uppercase text-tg-hint"
        >
          Географія пошуку
        </label>
        <select
          id="cold-search-work-format"
          name="workFormat"
          value={filters.workFormat}
          onChange={(e) =>
            setFilters((f) => ({
              ...f,
              workFormat: e.target.value as WorkFormat,
            }))
          }
          className="mt-1 w-full rounded border border-tg-secondary bg-tg-bg p-2 text-sm"
        >
          <option value="city">Місто вакансії</option>
          <option value="city_region">Місто та область</option>
          <option value="remote">Тільки віддалена</option>
        </select>
        <p className="mt-1 text-xs text-tg-hint">
          Це фільтр місця, не графіка роботи. «Тільки віддалена» шукає по всій
          країні й ігнорує місто.
        </p>
        <span className="mt-2 block text-xs uppercase text-tg-hint">
          Графік роботи
        </span>
        <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1">
          {SCHEDULE_OPTIONS.map((opt) => (
            <label
              key={opt.value}
              className="flex items-center gap-1 text-xs text-tg-text"
            >
              <input
                type="checkbox"
                name={`schedule-${opt.value}`}
                checked={filters.schedule.includes(opt.value)}
                onChange={(e) =>
                  setFilters((f) => ({
                    ...f,
                    schedule: e.target.checked
                      ? [...f.schedule, opt.value]
                      : f.schedule.filter((v) => v !== opt.value),
                  }))
                }
              />
              {opt.label}
            </label>
          ))}
        </div>
        <p className="mt-1 text-xs text-tg-hint">
          Що шукає сам кандидат. Нічого не позначено — не обмежуємо. Robota.ua
          фільтрує всі шість, Work.ua — лише «повна» і «неповна». Наша база
          читає графік під час розбору резюме, тому фільтрує лише розібрані
          після 23.08; раніші лишаються у видачі.
        </p>
        <label
          htmlFor="cold-search-scope"
          className="mt-2 block text-xs uppercase text-tg-hint"
        >
          Де шукати запит
        </label>
        <select
          id="cold-search-scope"
          name="searchScope"
          value={filters.searchScope}
          onChange={(e) =>
            setFilters((f) => ({
              ...f,
              searchScope: e.target.value as SearchScope,
            }))
          }
          className="mt-1 w-full rounded border border-tg-secondary bg-tg-bg p-2 text-sm"
        >
          <option value="default">З урахуванням синонімів</option>
          <option value="speciality">Тільки в назві резюме</option>
          <option value="everywhere">По всьому тексту резюме</option>
        </select>
        <p className="mt-1 text-xs text-tg-hint">
          Впливає лише на Robota.ua — у Work.ua один текстовий пошук.
        </p>
        <label
          htmlFor="cold-search-experience"
          className="mt-2 block text-xs uppercase text-tg-hint"
        >
          Досвід від (років)
        </label>
        <input
          id="cold-search-experience"
          name="experienceMin"
          type="number"
          inputMode="decimal"
          min={0}
          max={60}
          value={filters.experienceMin}
          onChange={(e) =>
            setFilters((f) => ({ ...f, experienceMin: e.target.value }))
          }
          placeholder="без обмеження"
          className="mt-1 w-full rounded border border-tg-secondary bg-tg-bg p-2 text-sm"
        />
        <label
          htmlFor="cold-search-rubric"
          className="mt-2 block text-xs uppercase text-tg-hint"
        >
          Рубрика (Robota.ua)
        </label>
        <select
          id="cold-search-rubric"
          name="specialization"
          value={filters.specialization}
          onChange={(e) =>
            setFilters((f) => ({ ...f, specialization: e.target.value }))
          }
          className="mt-1 w-full rounded border border-tg-secondary bg-tg-bg p-2 text-sm"
        >
          <option value="">Усі рубрики</option>
          {ROBOTA_RUBRICS.map((r) => (
            <option key={r.id} value={r.id}>
              {r.name}
            </option>
          ))}
        </select>
        <label
          htmlFor="cold-search-since"
          className="mt-2 block text-xs uppercase text-tg-hint"
        >
          Активність з дати
        </label>
        <input
          id="cold-search-since"
          name="sinceDate"
          type="date"
          value={sinceDate}
          onChange={(e) => {
            setSinceDate(e.target.value);
            setSinceTouched(true);
          }}
          className="mt-1 w-full rounded border border-tg-secondary bg-tg-bg p-2 text-sm"
        />
        {savedSince && (
          <p className="mt-1 text-xs text-tg-hint">
            Дату зафіксовано для цієї вакансії.
          </p>
        )}
        <p className="mt-1 text-xs text-tg-hint">
          Robota.ua відбирає за останньою активністю кандидата, тому на сайті
          резюме може бути датоване значно раніше. Work.ua відбирає за датою
          створення резюме. Змінена дата запамʼятовується для цієї вакансії;
          порожня — знімає зафіксовану й повертає типові {DEFAULT_SINCE_DAYS}{" "}
          днів.
        </p>
        <div className="mt-2 block text-xs uppercase text-tg-hint">Вік</div>
        <div className="flex gap-2">
          <label htmlFor="cold-search-age-min" className="sr-only">
            Вік від
          </label>
          <input
            id="cold-search-age-min"
            name="ageMin"
            type="number"
            inputMode="numeric"
            min={14}
            max={99}
            value={filters.ageMin}
            onChange={(e) =>
              setFilters((f) => ({ ...f, ageMin: e.target.value }))
            }
            placeholder="від"
            className="mt-1 w-full flex-1 rounded border border-tg-secondary bg-tg-bg p-2 text-sm"
          />
          <label htmlFor="cold-search-age-max" className="sr-only">
            Вік до
          </label>
          <input
            id="cold-search-age-max"
            name="ageMax"
            type="number"
            inputMode="numeric"
            min={14}
            max={99}
            value={filters.ageMax}
            onChange={(e) =>
              setFilters((f) => ({ ...f, ageMax: e.target.value }))
            }
            placeholder="до"
            className="mt-1 w-full flex-1 rounded border border-tg-secondary bg-tg-bg p-2 text-sm"
          />
        </div>
        <label
          htmlFor="cold-search-gender"
          className="mt-2 block text-xs uppercase text-tg-hint"
        >
          Стать
        </label>
        <select
          id="cold-search-gender"
          name="gender"
          value={filters.gender}
          onChange={(e) =>
            setFilters((f) => ({ ...f, gender: e.target.value }))
          }
          className="mt-1 w-full rounded border border-tg-secondary bg-tg-bg p-2 text-sm"
        >
          <option value="">Не важливо</option>
          <option value="male">Чоловіча</option>
          <option value="female">Жіноча</option>
        </select>
        <p className="mt-1 text-xs text-tg-hint">
          Вік і стать застосовуються до всіх джерел, включно з нашою базою.
          Кандидатів, у яких вік або стать невідомі, не відкидаємо. Порожні поля
          — без обмеження.
        </p>
        {vacancy.data &&
          (vacancy.data.work_format ||
            vacancy.data.gender_preference ||
            vacancy.data.age_min != null ||
            vacancy.data.age_max != null) && (
            <p className="mt-1 text-xs text-tg-hint">
              Обмеження підставлені з картки вакансії — саме їх застосовує
              нічний автопошук. Зміни тут діють лише на цей запуск.
            </p>
          )}
        {ageRangeInvalid && (
          <p className="mt-1 text-xs text-amber-700">
            «Вік до» не може бути меншим за «Вік від».
          </p>
        )}
        {unclaimed && (
          <p className="mt-2 text-xs text-amber-700">
            Спершу заберіть вакансію собі (вкладка «Вакансії» → «Забрати собі»)
            — тоді зможете запустити холодний пошук.
          </p>
        )}
        {vacancy.isError && (
          <p className="mt-2 text-xs text-amber-700">
            Не вдалося прочитати картку вакансії, тому поля вище порожні. Пошук
            піде за обмеженнями з картки; щоб змінити їх на цей запуск,
            відкрийте екран ще раз.
          </p>
        )}
        <button
          type="button"
          onClick={() => start.mutate()}
          disabled={
            start.isPending ||
            isSearching ||
            ageRangeInvalid ||
            unclaimed ||
            vacancyLoading
          }
          className="mt-3 w-full rounded bg-tg-button py-2 text-sm text-tg-buttonText disabled:opacity-50"
        >
          {start.isPending
            ? "Запускаю…"
            : isSearching
              ? "Пошук триває…"
              : vacancyLoading
                ? "Завантажую вакансію…"
                : "Запустити пошук"}
        </button>
      </div>

      {/* Without this the screen was mute on failure: a failed ``latest`` read
          leaves ``activeId`` null, and with it the status block, the toggle,
          the accumulated list and every row disappear — the recruiter saw a
          bare «Запустити пошук» form and read it as «нічого не знайдено». */}
      {!activeId && latest.isError && (
        <div className="rounded border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800">
          Не вдалося завантажити пошуки цієї вакансії:{" "}
          {(latest.error as Error).message}
          <div className="mt-1 text-xs">
            Якщо це повторюється — можливо, у вас немає доступу до цієї
            вакансії. Зверніться до адміністратора.
          </div>
        </div>
      )}
      {!activeId &&
        latest.isSuccess &&
        !mailingOnly &&
        !accumulated.isLoading &&
        (accumulated.isError ? (
          <div className="rounded border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800">
            Не вдалося прочитати кандидатів цієї вакансії:{" "}
            {(accumulated.error as Error).message}
          </div>
        ) : (
          <div className="rounded bg-tg-secondary p-3 text-xs text-tg-hint">
            За цією вакансією ще не було жодного холодного пошуку. Запустіть
            перший — форма вище.
          </div>
        ))}

      {showRunPanel && (
        <div className="rounded border border-tg-secondary p-3 text-sm">
          {isSearching ? (
            <div className="flex items-center gap-3">
              <span
                aria-hidden
                className="h-5 w-5 shrink-0 animate-spin rounded-full border-2 border-tg-secondary border-t-tg-button"
              />
              <div className="min-w-0">
                <div className="animate-pulse font-medium">
                  {coldSearch?.status === "running"
                    ? "Шукаю кандидатів і аналізую резюме…"
                    : "Пошук у черзі…"}
                </div>
                {elapsedSeconds !== null && elapsedSeconds >= 0 && (
                  <div className="text-xs tabular-nums text-tg-hint">
                    Триває {formatElapsed(elapsedSeconds)}
                  </div>
                )}
                <div className="text-xs text-tg-hint">
                  AI читає кожне резюме — зазвичай 2–20 хв, на широких запитах
                  довше. Можна повернутися сюди пізніше.
                </div>
              </div>
            </div>
          ) : detail.isError ? (
            <div className="text-amber-700">
              Не вдалося прочитати стан останнього пошуку:{" "}
              {(detail.error as Error).message}
            </div>
          ) : (
            <>
              {/* Без закріпленого прогону (лише розсилка) статусу немає, а не
                  «помилка». */}
              {coldSearch && (
                <div>
                  Статус:{" "}
                  {coldSearch.status === "completed" ? "завершено" : "помилка"}
                </div>
              )}
              {coldSearch && (
                <div className="text-xs text-tg-hint">
                  Знайдено {coldSearch.candidates_found} · AI проаналізував{" "}
                  {detail.data?.analyzed_count ?? 0}
                </div>
              )}
              {accumulatedData && (
                <div className="text-xs text-tg-hint">
                  У картці накопичено {accumulatedData.candidates_total ?? 0} ·
                  AI проаналізував {accumulatedData.analyzed_count ?? 0}
                  {showNewBadge
                    ? ` · за останній пошук додано ${accumulatedData.new_count ?? 0}`
                    : ""}
                </div>
              )}
              {typeof appliedMinScore === "number" &&
                appliedMinScore > 0 &&
                !thresholdRelaxed && (
                  <div className="text-xs text-tg-hint">
                    За порогом ≥ {appliedMinScore} показано{" "}
                    {Math.max(
                      0,
                      (totalMatched ?? results.length) - pendingCount,
                    )}
                    {pendingCount > 0 ? ` · без оцінки ${pendingCount}` : ""}
                  </div>
                )}
              {thresholdRelaxed && (
                <div className="mt-1 rounded border border-amber-300 bg-amber-50 p-2 text-xs text-amber-800">
                  Жоден кандидат не подолав поріг ≥ {appliedMinScore}. Показуємо{" "}
                  {Math.max(0, (totalMatched ?? results.length) - pendingCount)}{" "}
                  найкращих доступних — оцініть вручну.
                  {pendingCount > 0
                    ? ` Ще ${pendingCount} — без оцінки, резюме не отримали.`
                    : ""}
                </div>
              )}
              {hiddenTotal > 0 && (
                <div className="text-xs text-tg-hint">
                  Приховано {hiddenTotal}
                  {hiddenOutOfTown > 0
                    ? ` · не з міста вакансії: ${hiddenOutOfTown}`
                    : ""}
                  {hiddenOffField > 0
                    ? ` · резюме з іншої галузі: ${hiddenOffField}`
                    : ""}
                  {hiddenPrefiltered > 0
                    ? ` · з розсилки, ШІ відсіяв за назвою резюме: ${hiddenPrefiltered}`
                    : ""}
                  {hiddenOffSchedule > 0
                    ? ` · інший графік роботи: ${hiddenOffSchedule}`
                    : ""}
                  . Ті, кому вже писали, лишаються.
                </div>
              )}
              {accumulated.isError && (
                <div className="mt-1 text-xs text-amber-700">
                  {activeId
                    ? "Накопичений список тимчасово недоступний — показуємо лише останній пошук."
                    : "Не вдалося оновити список — показуємо останній прочитаний."}
                </div>
              )}
              {coldSearch?.status === "failed" && (
                <div className="text-red-600">
                  {coldSearch.error ?? "Помилка пошуку."}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {showRunPanel && (
        <div className="flex flex-col gap-2">
          <label className="flex items-center gap-2 text-xs text-tg-hint">
            <input
              type="checkbox"
              name="onlyLatest"
              checked={onlyLatest}
              // The two lists share the cursor and have nothing to do with each
              // other — page 3 of one is meaningless in the other.
              onChange={(e) => {
                setOnlyLatest(e.target.checked);
                setOffset(0);
              }}
              // Без закріпленого прогону «останнього пошуку» просто немає.
              disabled={accumulatedFailed || !activeId}
            />
            Лише останній пошук
          </label>
          <label className="flex items-center gap-2 text-xs text-tg-hint">
            <input
              type="checkbox"
              name="showDismissed"
              checked={showDismissed}
              // Не гейтимо на accumulatedFailed, як сусідню галочку: per-run
              // читання розуміє include_dismissed так само, тож у деградованому
              // режимі вона лишається робочою.
              onChange={(e) => {
                setShowDismissed(e.target.checked);
                setOffset(0);
              }}
            />
            Показати відхилених
          </label>
          <label className="flex items-center gap-2 text-xs text-tg-hint">
            <input
              type="checkbox"
              name="showBelowThreshold"
              checked={showBelowThreshold}
              // Тільки накопичене читання розуміє цей прапорець, тож у
              // деградованому per-run режимі галочка нічого не змінює.
              disabled={accumulatedFailed}
              onChange={(e) => {
                setShowBelowThreshold(e.target.checked);
                setOffset(0);
              }}
            />
            {belowThresholdHidden > 0
              ? `Показати нижче порога (${belowThresholdHidden})`
              : "Показати нижче порога"}
          </label>
          <label className="flex items-center gap-2 text-xs text-tg-hint">
            <input
              type="checkbox"
              name="showFilteredOut"
              checked={showFilteredOut}
              // Обидва читання розуміють цей прапорець, тож — на відміну від
              // сусіднього — він лишається робочим і в деградованому режимі.
              onChange={(e) => {
                setShowFilteredOut(e.target.checked);
                setOffset(0);
              }}
            />
            {hiddenFilteredOut > 0
              ? `Показати відсіяних (${hiddenFilteredOut})`
              : "Показати відсіяних"}
          </label>
        </div>
      )}

      {results.length > 0 && (
        <ul className="flex flex-col gap-2">
          {results.map((r, i) => {
            const meta = accumulatedMeta(r);
            // Картки без оцінки вже стоять останніми — цей рядок промовляє це
            // вголос. Без нього «резюме не отримали» їхало в тому ж списку, що
            // й оцінені, і читалось як ще один вибір ШІ.
            const opensPending =
              !r.match_score && (i === 0 || !!results[i - 1]?.match_score);
            return (
              <Fragment key={r.id}>
                {opensPending && (
                  <li className="rounded bg-tg-secondary p-2 text-[11px] text-tg-hint">
                    Нижче — ті, кого ШІ не оцінював: сайт не віддав тексту
                    резюме. Це не добірка, рішення за вами.
                  </li>
                )}
                <ResultCard
                  result={r}
                  vacancyId={vacancyId}
                  stages={stages}
                  defaultStageId={defaultStageId}
                  pushedStageName={pushedStageById[r.id]}
                  isNew={showNewBadge && meta.isNew}
                  firstSeenAt={showNewBadge ? meta.firstSeenAt : null}
                  onSend={(applicantStateId, stageName) => {
                    if (stageName !== undefined) {
                      setPushedStageById((m) => ({ ...m, [r.id]: stageName }));
                    }
                    send.mutate({
                      coldSearchId: r.cold_search_id,
                      resultId: r.id,
                      applicantStateId,
                    });
                  }}
                  onDismiss={() =>
                    dismiss.mutate({
                      coldSearchId: r.cold_search_id,
                      resultId: r.id,
                    })
                  }
                  tags={tagsQuery.data?.tags ?? []}
                  tagsFailed={tagsQuery.isError}
                  onAddToPool={(tagId, onFiled) =>
                    addToPool.mutate(
                      {
                        coldSearchId: r.cold_search_id,
                        resultId: r.id,
                        tagId,
                      },
                      { onSuccess: onFiled },
                    )
                  }
                  addingToPool={
                    addToPool.isPending &&
                    addToPool.variables?.resultId === r.id
                  }
                  sending={send.isPending && send.variables?.resultId === r.id}
                  dismissing={
                    dismiss.isPending && dismiss.variables?.resultId === r.id
                  }
                  mayOpenWorkUa={mayOpenWorkUa}
                  onWorkUaOpenSettled={invalidateResults}
                />
              </Fragment>
            );
          })}
        </ul>
      )}

      {/* A completed run with nothing to show must SAY so. Previously the row
          list simply did not render and the screen ended after the toggle. */}
      {showRunPanel && !running && !rowsFetching && results.length === 0 && (
        <div className="rounded bg-tg-secondary p-3 text-xs text-tg-hint">
          {offset > 0
            ? "На цій сторінці порожньо — поверніться назад."
            : "Кандидатів для показу немає. Спробуйте розширити запит або відсунути дату активності."}
        </div>
      )}

      {/* Outside the list block on purpose: an empty page must keep its way
          back, and the accumulated list grows without bound, so «показано 200
          з 350» has to be escapable from the phone. */}
      {(offset > 0 || pageRows.length >= RESULT_LIMIT) && (
        <div className="flex items-center justify-between gap-2 text-xs text-tg-hint">
          <button
            type="button"
            onClick={() => setOffset((o) => Math.max(0, o - RESULT_LIMIT))}
            disabled={offset === 0 || rowsFetching}
            className="rounded border border-tg-secondary px-3 py-2 disabled:opacity-40"
          >
            ← Назад
          </button>
          <span className="tabular-nums">
            {pageRows.length > 0
              ? `${offset + 1}–${offset + pageRows.length}`
              : "—"}
            {typeof totalMatched === "number" ? ` з ${totalMatched}` : ""}
          </span>
          <button
            type="button"
            onClick={() => setOffset((o) => o + RESULT_LIMIT)}
            disabled={pageRows.length < RESULT_LIMIT || rowsFetching}
            className="rounded border border-tg-secondary px-3 py-2 disabled:opacity-40"
          >
            Далі →
          </button>
        </div>
      )}
    </div>
  );
}

interface ResultCardProps {
  result: ColdSearchResult | VacancyColdSearchResult;
  /** The screen's vacancy — the candidate card opens with its AI analysis. */
  vacancyId: string;
  /** PeopleForce pipeline stages for the cold search's vacancy, already
   * sorted by position. Empty when the backend does not surface them —
   * the «Цікавий» flow then degrades to a candidate-only push. */
  stages: PipelineStage[];
  /** Server-resolved default stage id (position-1 «Новий кандидат»), or
   * ``null`` when no stages are available. */
  defaultStageId: number | null;
  /** Stage name chosen when this result was pushed (local, see parent). */
  pushedStageName: string | undefined;
  /** Surfaced for the first time by the last run — «Нове» badge. Already
   * suppressed by the parent while the vacancy has only one run. */
  isNew: boolean;
  /** Start of the earliest run that surfaced this candidate; ``null`` in the
   * per-run view, which has no such notion. */
  firstSeenAt: string | null;
  /** ``applicantStateId`` is omitted for a candidate-only push;
   * ``stageName`` is the chosen stage label to show on the card. */
  onSend: (applicantStateId: number | undefined, stageName?: string) => void;
  onDismiss: () => void;
  sending: boolean;
  dismissing: boolean;
  /** PeopleForce «Теги» the recruiter may file this candidate under. Empty
   * while loading or when the tenant has none — the button stays disabled. */
  tags: ApplicantTag[];
  tagsFailed: boolean;
  /** `onFiled` runs once PeopleForce has accepted the filing. */
  onAddToPool: (tagId: number, onFiled: () => void) => void;
  addingToPool: boolean;
  mayOpenWorkUa: boolean;
  onWorkUaOpenSettled: () => void;
}

function ResultCard({
  result,
  vacancyId,
  stages,
  defaultStageId,
  pushedStageName,
  isNew,
  firstSeenAt,
  onSend,
  onDismiss,
  sending,
  dismissing,
  tags,
  tagsFailed,
  onAddToPool,
  addingToPool,
  mayOpenWorkUa,
  onWorkUaOpenSettled,
}: ResultCardProps): JSX.Element {
  // Видно лише під «Показати відхилених».
  const dismissed = isDismissed(result);
  const origin = originSource(result);
  // Анонс із розсилки: резюме тут немає, і «Відкрити резюме» його не дасть —
  // текст для ШІ приходить лише з платним відкриттям контакту.
  const awaitsWorkUaOpen =
    workUaOpenApplies(result) && !workUaOpenSettledPaid(result);

  return (
    <li className="flex flex-col gap-2 rounded border border-tg-secondary p-3 text-sm">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Link
              to={`/candidate/${result.candidate.id}?vacancy=${vacancyId}`}
              className="truncate font-medium text-tg-link"
            >
              {result.candidate.full_name}
            </Link>
            {isNew && (
              <span className="shrink-0 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-medium text-emerald-800">
                Нове
              </span>
            )}
            {dismissed && (
              <span className="shrink-0 rounded-full bg-rose-100 px-2 py-0.5 text-[10px] font-medium text-rose-800">
                Відхилений
              </span>
            )}
          </div>
          <div className="text-xs text-tg-hint">
            ранг {result.rank} · {sourceLabel(origin)}
            {foundInOurDatabase(result) && !isInternalOrigin(origin)
              ? " (з нашої бази)"
              : ""}
            {result.candidate.city ? ` · ${result.candidate.city}` : ""}
          </div>
          {result.outside_vacancy_city && (
            <div className="text-xs font-medium text-amber-700">
              не з міста вакансії
            </div>
          )}
          {result.candidate.headline && (
            // Заголовок резюме з рук кандидата. На картці без оцінки це єдине,
            // що взагалі пояснює людину: «Linux system administrator» під
            // вакансією «Мийник деталей» відповідає без відкривання сайту.
            <div className="text-xs text-tg-hint">
              {result.candidate.headline}
            </div>
          )}
          {firstSeenAt && !isNew && (
            <div className="text-xs text-tg-hint">
              вперше знайдено {shortDate(firstSeenAt)}
            </div>
          )}
          {result.prefilter_reason && (
            <div className="text-xs text-amber-700">
              ШІ відсіяв за назвою резюме: {result.prefilter_reason}
            </div>
          )}
        </div>
        {result.match_score ? (
          <ScoreBadge
            score={result.match_score.score}
            scaleMax={result.match_score.scale_max}
            category={result.match_score.category}
          />
        ) : result.status === "needs_cv" ? (
          <span className="shrink-0 whitespace-nowrap rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-800">
            резюме не отримали
          </span>
        ) : (
          <span className="shrink-0 whitespace-nowrap rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-700">
            без оцінки
          </span>
        )}
      </div>

      {result.status === "needs_cv" && (
        // No tooltip on a phone, so the reason goes on its own line: without
        // it «резюме не отримали» looks like our failure rather than a thin
        // profile the board never filled in.
        <div className="text-xs text-tg-hint">
          {awaitsWorkUaOpen
            ? "Work.ua дав лише анонс — посада, вік, місто. ШІ оцінить резюме після «Відкрити контакт»."
            : "Сайт віддав лише короткий профіль — посада, вік, місто. Відкрийте резюме, щоб оцінити."}
        </div>
      )}

      <ColdResultActions
        result={result}
        vacancyId={vacancyId}
        stages={stages}
        defaultStageId={defaultStageId}
        pushedStageName={pushedStageName}
        onSend={onSend}
        onDismiss={onDismiss}
        sending={sending}
        dismissing={dismissing}
        tags={tags}
        tagsFailed={tagsFailed}
        onAddToPool={onAddToPool}
        addingToPool={addingToPool}
        mayOpenWorkUa={mayOpenWorkUa}
        onWorkUaOpenSettled={onWorkUaOpenSettled}
      />
    </li>
  );
}
